import PropTypes from "prop-types";
import { formatCurrency } from "@helpers/utils";
import ProgressBar from "./progress-bar";

const LegendItem = ({ dotClassName, label, value }) => (
    <div className="flex items-center gap-2 text-sm">
        <span className={`h-2 w-2 rounded-full ${dotClassName}`} />
        <span className="text-muted">{label}</span>
        <span className="font-medium text-gray-900">{value}</span>
    </div>
);

LegendItem.propTypes = {
    dotClassName: PropTypes.string.isRequired,
    label       : PropTypes.string.isRequired,
    value       : PropTypes.node
};

const DebitNoteCell = ({ label, amount }) => (
    <div className="rounded-lg bg-light/60 px-3 py-2.5">
        <p className="text-xs text-muted">{label}</p>
        <p className="mt-0.5 text-sm font-semibold text-gray-900">{amount}</p>
    </div>
);

DebitNoteCell.propTypes = {
    label : PropTypes.string.isRequired,
    amount: PropTypes.node
};

// One block per currency — the API never sums across currencies. The debit-note row
// is managed-payout only: a self-payout brand never has one raised against it.
const AmountsCard = ({ amounts, showDebitNotes }) => (
    <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
        <h3 className="text-sm font-semibold text-gray-900">Publisher invoices</h3>

        {amounts.length === 0 && <p className="py-8 text-center text-sm text-muted">No invoices yet.</p>}

        <div className="divide-y divide-gray-100">
            {amounts.map((row) => (
                <div key={row.currency} className="py-4 last:pb-0">
                    <div className="flex items-baseline justify-between gap-3">
                        <p className="text-2xl font-semibold tracking-tight text-gray-900">{formatCurrency(row.total_invoiced, row.currency)}</p>
                        <span className="rounded-md bg-light px-2 py-0.5 text-xs font-semibold text-muted">{row.currency}</span>
                    </div>
                    <p className="text-xs text-muted">Total invoiced</p>

                    <div className="mt-4">
                        <ProgressBar value={row.paid} total={row.total_invoiced} />
                    </div>
                    <div className="mt-3 flex flex-wrap gap-x-6 gap-y-1">
                        <LegendItem dotClassName="bg-success" label="Paid" value={formatCurrency(row.paid, row.currency)} />
                        <LegendItem dotClassName="bg-gray-300" label="Pending" value={formatCurrency(row.pending, row.currency)} />
                    </div>

                    {showDebitNotes && (
                        <div className="mt-4 grid grid-cols-1 gap-2 sm:grid-cols-3">
                            <DebitNoteCell label="Debit notes raised" amount={formatCurrency(row.debit_note_total, row.currency)} />
                            <DebitNoteCell label="Active" amount={formatCurrency(row.debit_note_active, row.currency)} />
                            <DebitNoteCell label="Settled" amount={formatCurrency(row.debit_note_settled, row.currency)} />
                        </div>
                    )}
                </div>
            ))}
        </div>
    </div>
);

AmountsCard.propTypes = {
    amounts       : PropTypes.arrayOf(PropTypes.object).isRequired,
    showDebitNotes: PropTypes.bool
};

export default AmountsCard;
